import React, { useMemo, useRef, useState } from 'react';
import { X, Lock, Upload, Trash2 } from 'lucide-react';
import axios, { API } from '../../utils/api';
import notify from '../../utils/notify';
import { eventSeatsTaken } from '../../utils/communityMembership';

const TEAL = '#4DB6AC';
// Same limits as the community Create Event form.
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const FIELDS = {
  free: ['description', 'image_url', 'pocName', 'pocEmail'],
  material: ['start_date', 'end_date', 'start_time', 'end_time', 'location', 'meetLink', 'eventMode', 'frequency', 'recurrence_end_date'],
  locked: ['reward_stars', 'verification_method', 'challenge_type', 'is_realtime', 'event_type'],
};
const LABELS = {
  description: 'Description', image_url: 'Image', pocName: 'Contact name', pocEmail: 'Contact email',
  start_date: 'Start date', end_date: 'End date', start_time: 'Start time', end_time: 'End time',
  location: 'Venue / location', meetLink: 'Online link', eventMode: 'Mode', frequency: 'Repeat',
  recurrence_end_date: 'Repeat end date', reward_stars: 'Reward stars', verification_method: 'Verification method',
  challenge_type: 'Challenge type', is_realtime: 'Real-time event', event_type: 'Event type', max_participants: 'Maximum seats',
};
const SELECTS = {
  eventMode: [['inperson', 'In-person'], ['online', 'Online']],
  frequency: [['once', 'Once'], ['weekly', 'Weekly'], ['monthly', 'Monthly']],
  verification_method: [['manual', 'Manual'], ['photo', 'Photo'], ['geotag', 'Geotag'], ['both', 'Photo + Geotag']],
  challenge_type: [['Fun & Sport', 'Fun & Sport'], ['Learning & Growth', 'Learning & Growth'], ['Creativity', 'Creativity'], ['Entertainment', 'Entertainment']],
  event_type: [['event', 'Event'], ['voting', 'Voting']],
};
const TYPES = { start_date: 'date', end_date: 'date', recurrence_end_date: 'date', start_time: 'time', end_time: 'time', reward_stars: 'number' };

const initialValue = (ev, k) => {
  const v = ev[k];
  if (k === 'start_date' || k === 'end_date') return (v || ev.event_date || '').slice(0, 10);
  if (k === 'is_realtime') return !!v;
  return v ?? '';
};

export const CommunityEventEditModal = ({ event, token, onClose, onSaved }) => {
  const all = [...FIELDS.free, ...FIELDS.material, ...FIELDS.locked, 'max_participants'];
  const initial = useMemo(() => Object.fromEntries(all.map(k => [k, initialValue(event, k)])), [event]);
  const [form, setForm] = useState(initial);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) { setUploadError('Only JPG and PNG images are supported.'); return; }
    if (file.size > MAX_IMAGE_BYTES) { setUploadError('Image is too large. Max size is 5MB.'); return; }
    setUploadError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/community/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: res.data.url }));
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Image upload failed.');
    } finally { setUploading(false); }
  };

  const imageControl = (
    <div key="image_url" style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Image</label>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png" data-testid="edit-event-image-file" onChange={handleFile} style={{ display: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {form.image_url
          ? <img data-testid="edit-event-image-preview" src={form.image_url} alt="" style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }} />
          : <div data-testid="edit-event-image-empty" style={{ width: 72, height: 54, borderRadius: 8, background: '#F3F4F6', fontSize: 10, color: '#9CA3AF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No image</div>}
        <button type="button" data-testid="edit-event-image-replace" disabled={uploading} onClick={() => fileRef.current?.click()}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 10px', borderRadius: 8, border: `1px solid ${TEAL}`, background: '#fff', color: TEAL, fontSize: 12, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer' }}>
          <Upload size={12} /> {uploading ? 'Uploading...' : form.image_url ? 'Replace image' : 'Upload image'}
        </button>
        {form.image_url && (
          <button type="button" data-testid="edit-event-image-remove" disabled={uploading} onClick={() => setForm(f => ({ ...f, image_url: '' }))}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 10px', borderRadius: 8, border: '1px solid #FCA5A5', background: '#fff', color: '#B91C1C', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <Trash2 size={12} /> Remove image
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>JPG or PNG, up to 5MB.</div>
      {uploadError && <div data-testid="edit-event-image-error" style={{ fontSize: 12, color: '#B91C1C', marginTop: 4 }}>{uploadError}</div>}
    </div>
  );
  const registered = new Set(event.attendees || []).size;
  const taken = eventSeatsTaken(event);
  const locked = taken > 0;
  const changed = all.filter(k => String(form[k]) !== String(initial[k]));
  const materialChanged = changed.filter(k => FIELDS.material.includes(k));

  const save = async () => {
    if (materialChanged.length && !reason.trim()) { notify.error('Please give a reason for this change.'); return; }
    const body = {};
    changed.forEach(k => {
      let v = form[k];
      if (k === 'reward_stars') v = v === '' ? 0 : parseInt(v, 10);
      if (k === 'max_participants') v = v === '' ? null : parseInt(v, 10);
      if (v === '' && k !== 'description') v = null;
      body[k] = v;
    });
    if ('start_date' in body) body.event_date = body.start_date;
    if (materialChanged.length) body.reason = reason.trim();
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/community/admin/events/${event.id}`, body, { headers: { Authorization: `Bearer ${token}` } });
      notify.success(res.data.changed_fields?.length ? `Saved: ${res.data.changed_fields.join(', ')}` : 'No changes');
      onSaved(res.data.event);
    } catch (err) {
      notify.error(err.response?.data?.detail || 'Failed to save changes');
    } finally { setSaving(false); }
  };

  const input = (k) => {
    if (k === 'image_url') return imageControl;
    const isLocked = locked && FIELDS.locked.includes(k);
    const common = { 'data-testid': `edit-event-${k}`, disabled: isLocked, style: { width: '100%', padding: '9px 10px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 13, boxSizing: 'border-box', background: isLocked ? '#F3F4F6' : '#fff' } };
    let el;
    if (k === 'is_realtime') el = <input type="checkbox" checked={!!form[k]} onChange={e => setForm({ ...form, [k]: e.target.checked })} {...common} style={{}} />;
    else if (SELECTS[k]) el = <select value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} {...common}>{SELECTS[k].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
    else if (k === 'description') el = <textarea rows={3} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} {...common} />;
    else el = <input type={TYPES[k] || (k === 'max_participants' ? 'number' : 'text')} min={k === 'max_participants' ? 0 : undefined} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} {...common} />;
    return (
      <div key={k} style={{ marginBottom: 10 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{LABELS[k]}</label>
        {el}
        {k === 'pocEmail' && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>Visible to all members.</div>}
        {k === 'max_participants' && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>Empty or 0 = no limit. Can't go below {taken} seats taken.</div>}
        {isLocked && <div data-testid={`edit-event-locked-${k}`} style={{ fontSize: 11, color: '#92400E', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><Lock size={11} /> Locked because people have already registered.</div>}
      </div>
    );
  };

  return (
    <div data-testid="community-event-edit-modal" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Edit: {event.title}</h3>
          <button data-testid="edit-event-close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>{registered} registered · {taken} seats taken</div>
        <h4 style={{ fontSize: 13, margin: '6px 0' }}>Details</h4>
        {FIELDS.free.map(input)}
        <h4 style={{ fontSize: 13, margin: '12px 0 6px' }}>Date, time and place <span style={{ fontWeight: 400, color: '#6B7280' }}>(only before the event starts)</span></h4>
        {FIELDS.material.map(input)}
        {input('max_participants')}
        <h4 style={{ fontSize: 13, margin: '12px 0 6px' }}>Rules</h4>
        {FIELDS.locked.map(input)}
        {materialChanged.length > 0 && (
          <div data-testid="edit-event-material-warning" style={{ background: '#FEF8F0', border: '1px solid #F28C2840', borderRadius: 10, padding: 10, margin: '10px 0' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#92400E', marginBottom: 6 }}>{registered} registered {registered === 1 ? 'person' : 'people'} will be notified</div>
            <textarea data-testid="edit-event-reason" rows={2} placeholder="Reason for the change (required)" value={reason} onChange={e => setReason(e.target.value)}
              style={{ width: '100%', padding: 8, borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
        )}
        <button data-testid="edit-event-save" onClick={save} disabled={saving || uploading || changed.length === 0}
          style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: changed.length ? TEAL : '#E5E7EB', color: '#fff', fontWeight: 700, fontSize: 14, cursor: changed.length ? 'pointer' : 'default', marginTop: 6 }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default CommunityEventEditModal;
